import * as React from 'react';
import { ApolloError } from 'apollo-client';
import { Query } from 'react-apollo';
import update from 'immutability-helper';

import GET_COACH_LIST_QUERY from 'coaching/queries/Coaches.gql';
import { Coaches, Coaches_coaching_coaches_coaches } from 'coaching/queries/__generated__/Coaches';
import { useRef } from 'react';

export type Props = {
  children: (props: {
    coaches?: Coaches_coaching_coaches_coaches[];
    loading: boolean;
    error?: ApolloError;
    onNextPage: (after?: string | null) => Promise<void>;
    endCursor?: string | null;
    hasNextPage?: boolean;
  }) => React.ReactNode;
  query: string;
  skipQuery?: boolean;
};

export default function GetCoachesContainer(props: Props) {
  const currentNameQuery = useRef<string>('');
  return (
    <Query<Coaches>
      query={GET_COACH_LIST_QUERY}
      variables={{ nameQuery: props.query }}
      skip={props.skipQuery}>
      {({ loading, error, data, fetchMore, ...others }) => {
        currentNameQuery.current = others.variables.nameQuery;

        const handleNextPage = async (after?: string | null) => {
          await fetchMore({
            variables: {
              after,
              nameQuery: props.query,
            },
            updateQuery: (previousResult, { fetchMoreResult, variables }) => {
              if (!fetchMoreResult) {
                return previousResult;
              }
              if (currentNameQuery.current !== variables?.nameQuery) {
                return previousResult;
              }

              const existingCoaches = previousResult.coaching_coaches?.coaches ?? [];
              const newCoaches = fetchMoreResult.coaching_coaches?.coaches ?? [];

              return update(previousResult, {
                coaching_coaches: {
                  pageInfo: {
                    $set: fetchMoreResult.coaching_coaches?.pageInfo,
                  },
                  coaches: { $set: [...existingCoaches, ...newCoaches] },
                },
              });
            },
          });
        };

        return props.children({
          coaches: data?.coaching_coaches?.coaches,
          loading,
          error,
          endCursor: data?.coaching_coaches?.pageInfo?.endCursor,
          hasNextPage: data?.coaching_coaches?.pageInfo?.hasNextPage,
          onNextPage: handleNextPage,
        });
      }}
    </Query>
  );
}
